// Row layout for `integration` + `plugin` kinds (MarketplaceCard handles
// library + template with thumbnails). Integrations are service
// connectors — name + logo + one-line description + Configure button
// reads cleanly as a scannable row.
//
// `gridColumn: 'span 2'` pairs two rows per grid row in the
// marketplace's 4-column grid, giving rows breathing space without
// collapsing to a single item per row.

export const MarketplaceIntegrationRow = {
  // `flow` already emits `display: flex` — no need to extends Flex.
  flow: 'x',
  align: 'center',
  gap: 'Z',
  padding: 'Z A',
  gridColumn: 'span 2',
  borderRadius: 'Z',
  border: '1px solid',
  cursor: 'pointer',
  transition: 'Z defaultBezier border-color, Z defaultBezier background',
  background: (el) => {
    const host = el.call('marketplaceHostOf', el)
    return host?.selectedItem?.id === el.id ? 'blue.1' : 'dialog'
  },
  borderColor: (el) => {
    const host = el.call('marketplaceHostOf', el)
    return host?.selectedItem?.id === el.id ? 'blue' : 'line'
  },
  ':hover': { borderColor: 'blue', background: 'dialog' },

  // Filter lives in functions/index.js#matchesFilters — the grid's
  // children function projects catalog → visible items upstream.
  // No `hide:` predicate needed here.

  // A11y + keyboard: tabindex=0 + role=button so Tab reaches the row
  // and assistive tech announces it. Enter/Space open detail.
  tabindex: '0',
  role: 'button',
  ':focus-visible': {
    outline: '2px solid blue',
    outlineOffset: '2px'
  },

  // Fresh journey from the grid — clears selectedHistory. Similar-item
  // clicks in the detail pane use selectItemWithHistory instead.
  onClick: (e, el) => el.call('openDetailFromEl', el),
  onKeydown: (e, el) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    el.call('openDetailFromEl', el)
  },

  RowLogo: {
    align: 'center center',
    width: 'B',
    height: 'B',
    minWidth: 'B',
    borderRadius: 'Y',
    overflow: 'hidden',
    fontWeight: '700',
    fontSize: 'A',
    background: (el) => el.parent.logo ? 'white' : el.call('getKindAccent', el.parent.kind, 'bg'),
    color: (el) => el.call('getKindAccent', el.parent.kind, 'fg'),

    // Nested DOMQL img/letter pattern — Rule 31 (no html: strings with
    // inline style=). Mirrors MarketplaceDetailSimilarItem.SimilarLogo.
    RowLogoImg: {
      // Guard: logo must be a non-null, non-empty string.
      hide: (el) => !el.parent.parent.logo || el.parent._imgFailed,
      tag: 'img',
      src: (el) => {
        const slug = el.parent.parent.logo
        return slug ? `https://cdn.simpleicons.org/${slug}` : ''
      },
      alt: (el) => el.parent.parent.name,
      width: '60%',
      height: '60%',
      objectFit: 'contain',
      // onerror fallback: CDN 404 → flag the parent logo container so
      // the reactive `hide` on both img and letter re-evaluates correctly
      // on the next render cycle. DOM-level fallback ensures immediate
      // visual correction even before the next DOMQL render pass.
      onRender: (el) => {
        if (!el.node) return
        el.node.onerror = () => {
          el.parent._imgFailed = true
          el.node.style.display = 'none'
          const letter = el.parent.RowLogoLetter
          if (letter?.node) letter.node.style.display = ''
        }
      }
    },
    RowLogoLetter: {
      // Show when there's no logo OR when the img errored out.
      hide: (el) => !!el.parent.parent.logo && !el.parent._imgFailed,
      tag: 'span',
      text: (el) => (el.parent.parent.name || '')[0]?.toUpperCase() || '?'
    }
  },

  RowInfo: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',

    RowTitle: {
      tag: 'h5',
      text: (el) => el.parent.parent.name,
      fontSize: 'Z',
      fontWeight: '600',
      color: 'title',
      margin: '0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },

    RowDesc: {
      tag: 'p',
      text: (el) => el.parent.parent.description,
      fontSize: 'Y',
      color: 'caption',
      margin: '0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  },

  RowActions: {
    flow: 'x',
    align: 'center',
    gap: 'Z',
    flexShrink: '0',

    // Pin / unpin star. Pattern mirrors CardPinBtn on the card; this
    // row layout has no thumbnail overlay so the star sits inline
    // with the price + install button actions.
    RowPinBtn: {
      extends: 'Button',
      padding: 'X Y',
      borderRadius: 'Y',
      background: 'transparent',
      color: (el) => el.call('isPinned',
        el.call('marketplaceHostOf', el),
        el.parent.parent.id
      ) ? 'yellow' : 'caption',
      fontSize: 'Z',
      text: (el) => el.call('isPinned',
        el.call('marketplaceHostOf', el),
        el.parent.parent.id
      ) ? '★' : '☆',
      title: 'Pin to sidebar',
      ariaLabel: 'Toggle pin',
      onClick: (e, el) => {
        e.stopPropagation()
        const host = el.call('marketplaceHostOf', el)
        el.call('togglePin', host, el.parent.parent.id)
      }
    },

    RowPrice: {
      tag: 'span',
      hide: (el) => !el.parent.parent.price,
      text: (el) => `$${el.parent.parent.price}`,
      fontSize: 'Y',
      fontWeight: '500',
      color: 'title'
    },

    RowInstall: {
      extends: 'Button',
      text: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return el.call('installButtonText', el.parent.parent, host)
      },
      disabled: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return el.call('installButtonDisabled', el.parent.parent, host)
      },
      onClick: (e, el) => {
        e.stopPropagation()
        const host = el.call('marketplaceHostOf', el)
        return el.call('dispatchInstallAndMark', el.parent.parent, host)
      }
    }
  }
}
